import React, { useCallback, useContext, useState } from "react";
import { useGet } from "../api/BaseEntityApi";
import { adminStatisticsConstants, AdminStatisticsReadModel } from "../api/Admin";
import SinglePage from "../common/panels/SinglePage/SinglePage";
import { LoadingWrapper } from "../common/LoadingWrapper";
import { SessionContext } from "../common/contexts/SessionContext";
import { PageNotAllowed } from "../main/common/PageNotAllowed/PageNotAllowed";
import { DateTimeRenderer } from "../common/datetime/DateTimeFormatter";
import { formatBytes } from "../Dataset/common/Tree/Folder/helpers/FileHelpers";
import { useVersionInfo } from "../About/About";
import { ContentTable } from "../common/tables/ContentTable/ContentTable";
import { Tile } from "../common/panels/Tile/Tile";
import { camelCaseToWords, toUppercase } from "../common/helperfunctions/stringFunctions";
import { RenderTabStoreHistoryButton } from "../common/tables/Tabs/history/TabStoreHistory";
import { DatasetFilters, datasetsConstants } from "../api/Datasets";
import { DatasetsFilterForm, datasetsFiltersToForm } from "../Dataset/Table/DatasetsFilterBar";
import { LucideIcon } from "../common/icon/LucideIcon";
import { useHistory } from "react-router-dom";
import { getIndexRoute } from "../main/Routing";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../api/GenericConstants";
import { Button } from "../common/buttons/Button/Button";
import { CsvSettings } from "../common/tables/ExportCsvButton/useDownloadCSV";
import { showtoast } from "../common/overlays/Toasts/showtoast";
import { useTitle } from "../common/helperfunctions/useTitle";

export const useDownloadStatisticsCSV = ({
  csvSettings = {
    delimiter: ";",
    listDelimiter: ",",
    nullValue: "-",
    propertyDelimiter: ":",
    csvObjectLevel: "Name",
    includeHeader: true,
  },
  fileNameOverride,
}: {
  csvSettings?: CsvSettings;
  fileNameOverride?: string;
}) => {
  const { api } = useContext(SessionContext);
  const [loading, setLoading] = useState<boolean>(false);

  const downloadCSV = useCallback(async () => {
    setLoading(true);
    try {
      api
        .post(`${adminStatisticsConstants.resource}/export/csv`, csvSettings)
        .then((result) => {
          var blob = new Blob([result], { type: "application/csv;charset=utf-8" });
          var url = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.target = "_blank";
          anchor.download = !!fileNameOverride
            ? `${fileNameOverride}.csv`
            : `${adminStatisticsConstants.entityPlural.replaceAll(" ", "_")}.csv`;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        })
        .catch((e) => {
          showtoast("error", "An error occurred while processing your request");
          console.error(e);
        });

      setLoading(false);
    } catch {
      showtoast("error", "An error occurred while processing your request");
    }
    setLoading(false);
  }, [api, csvSettings, fileNameOverride]);

  return { downloadCSV, loading };
};

export const AdminStatisticsTable = () => {
  const history = useHistory();
  const { session, route, group } = useContext(SessionContext);
  const { data, status, fetchStatus, error } = useGet<AdminStatisticsReadModel>("admin/statistics");
  const { infoMapping, additionalFeatures } = useVersionInfo();
  useTitle(`LOGS - ${toUppercase(adminStatisticsConstants.entityPlural)}`); // Set the page title

  const { downloadCSV, loading } = useDownloadStatisticsCSV({ fileNameOverride: "admin_statistics" });

  if (
    ![
      !!session?.permissions.logsadmin,
      !!session?.permissions.view_admin_pages,
      !!session?.permissions.view_global_audit_log,
    ].some((p) => p)
  )
    return <PageNotAllowed />;
  return (
    <SinglePage>
      <SinglePage.Head>
        <SinglePage.Head.Label>
          <div className="flex row-nowrap align-center gap-5">
            <LucideIcon name="chart-bar" style={{ color: "var(--primary)", width: 20, height: 20 }} /> Statistics for
            <code>/{group}</code>
          </div>
        </SinglePage.Head.Label>
        <SinglePage.Head.Controls>
          <Button
            className="btn btn-default"
            style={{ width: "max-content" }}
            loading={loading}
            onClick={() => downloadCSV()}
            title={`Export ${adminStatisticsConstants.entityPlural} to CSV`}
          >
            <LucideIcon name="download" /> Export to CSV
          </Button>
        </SinglePage.Head.Controls>
      </SinglePage.Head>
      <SinglePage.Content style={{ height: "100%", overflow: "hidden" }}>
        <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
          {data && (
            <div className="flex col-nowrap gap-5" style={{ height: "fit-content", overflow: "auto", padding: 10 }}>
              <div>
                <React.Fragment>
                  {infoMapping && <ContentTable content={infoMapping} />}
                  {additionalFeatures && (
                    <React.Fragment>
                      <Tile isFolded>
                        <Tile.Head title="Features" />
                        <Tile.Body>
                          <ContentTable content={additionalFeatures} />
                        </Tile.Body>
                      </Tile>
                    </React.Fragment>
                  )}
                </React.Fragment>
              </div>

              <div className="flex col-nowrap">
                <h2>Entities</h2>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Entity</th>
                      <th>Count</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.entityCounts).map(([entity, count]) => (
                      <tr key={entity}>
                        <td>{toUppercase(camelCaseToWords(entity))}</td>
                        <td>{count}</td>
                        <td>
                          <button
                            className="btn btn-xs btn-default"
                            onClick={() =>
                              history.push(
                                route(
                                  getIndexRoute(
                                    genericEntityConstants[entity as GenericEntityConstantsEntities].entityConstants
                                      .frontendIndexRoute
                                  )
                                )
                              )
                            }
                          >
                            <LucideIcon name="table" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex col-nowrap">
                <h2>Persons stats</h2>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Persons</th>
                      <th>Persons with user account</th>
                      <th>Persons without user account</th>
                      <th>Persons with SSO enabled</th>
                      <th>Persons with 2FA enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{data.personStatistics.personCount}</td>
                      <td>{data.personStatistics.personWithUserCount}</td>
                      <td>{data.personStatistics.personWithoutUserCount}</td>
                      <td>{data.personStatistics.personWithSSOCount}</td>
                      <td>{data.personStatistics.personWith2FACount}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="flex col-nowrap">
                <h2>Data format stats</h2>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Parser</th>
                      <th>Dataset count</th>
                      <th>Last creation date</th>
                      <th>Last entered on date</th>
                      <th>Accumulated dataset size</th>
                      <th>Accumulated cache size</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.fileFormatStatistics).map(([parser, stats]) => (
                      <tr key={parser}>
                        <td>{parser}</td>
                        <td>{stats.datasetCount}</td>
                        <td>{<DateTimeRenderer date={stats.lastCreationDate} />}</td>
                        <td>{<DateTimeRenderer date={stats.lastEnteredOnDate} />}</td>
                        <td>{formatBytes(stats.accumulatedDatasetSize)}</td>
                        <td>{formatBytes(stats.accumulatedCacheSize)}</td>
                        <td>
                          <RenderTabStoreHistoryButton<DatasetFilters, DatasetsFilterForm>
                            entityConstants={datasetsConstants}
                            filters={{ formatIds: [parser] }}
                            filtersToForm={datasetsFiltersToForm}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </LoadingWrapper>
      </SinglePage.Content>
    </SinglePage>
  );
};
